import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, X, Loader2, AlertTriangle, Mail, Camera } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  active: '#22c55e', inactive: '#ef4444', graduated: '#6b7280',
  current: '#22c55e', overdue: '#ef4444',
  paid: '#22c55e', pending: '#f59e0b',
  checked_in: '#22c55e', checked_out: '#6b7280', absent: '#ef4444',
  happy: '#22c55e', tired: '#f59e0b', upset: '#ef4444', calm: '#3b82f6',
  daily: '#3b82f6', incident: '#ef4444',
  in_stock: '#22c55e', low: '#ef4444', out_of_stock: '#6b7280',
  sent: '#22c55e', system: '#3b82f6', sms: '#8b5cf6', email: '#f59e0b',
};

export default function DaycareModule({ moduleKey, moduleName, tenantId, themeColor }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [parentsList, setParentsList] = useState([]);
  const [inviteResult, setInviteResult] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const photoRef = useRef(null);

  const accent = themeColor || '#FACC15';

  // Module aliases: some tenants may have legacy nav keys we map to canonical modules.
  const MODULE_ALIASES = { tuition: 'payments', daily_reports: 'reports' };
  const effectiveModuleKey = MODULE_ALIASES[moduleKey] || moduleKey;
  const columns = MODULE_CONFIGS[effectiveModuleKey] || MODULE_CONFIGS.children;

  // Modules that support a single photo upload tied to the saved item.
  const PHOTO_MODULES = { children: 'children', reports: 'reports' };
  const supportsPhoto = !!PHOTO_MODULES[effectiveModuleKey];

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/daycare/${tenantId}/${effectiveModuleKey}`, { params: { search } });
      setItems(res.data);
    } catch (err) {
      console.error(`Error fetching ${effectiveModuleKey}:`, err);
    }
    setLoading(false);
  }, [tenantId, effectiveModuleKey, search]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Fetch parents list for children form
  useEffect(() => {
    if (effectiveModuleKey === 'children' && tenantId) {
      axios.get(`${API}/daycare/${tenantId}/parents`).then(res => setParentsList(res.data)).catch(() => {});
    }
  }, [effectiveModuleKey, tenantId]);

  const openNew = () => {
    setEditingItem(null);
    const defaults = {};
    columns.forEach(col => {
      if (col.default !== undefined) defaults[col.key] = col.default;
    });
    setFormData(defaults);
    setPhotoFile(null);
    setPhotoPreview('');
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setPhotoFile(null);
    setPhotoPreview(item.photo_url ? `${process.env.REACT_APP_BACKEND_URL}${item.photo_url}` : '');
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      let savedItem;
      if (editingItem) {
        const res = await axios.put(`${API}/daycare/${tenantId}/${effectiveModuleKey}/${editingItem.id}`, formData);
        savedItem = res.data;
        toast.success('Updated successfully');
      } else {
        const res = await axios.post(`${API}/daycare/${tenantId}/${effectiveModuleKey}`, formData);
        savedItem = res.data;
        toast.success('Created successfully');
        // Show generated password for staff
        if (effectiveModuleKey === 'staff' && savedItem?.generated_password) {
          setInviteResult({
            name: `${savedItem.first_name} ${savedItem.last_name}`,
            email: savedItem.email,
            temp_password: savedItem.generated_password,
            portal_url: `${window.location.origin}/${localStorage.getItem('tenant_slug') || ''}/login`,
            kind: 'staff',
          });
        }
        // Show generated password for parents (when portal access was created)
        if (effectiveModuleKey === 'parents' && savedItem?.generated_password) {
          setInviteResult({
            name: `${savedItem.first_name} ${savedItem.last_name}`,
            email: savedItem.portal_email || savedItem.email,
            temp_password: savedItem.generated_password,
            portal_url: savedItem.portal_url || `${window.location.origin}/${localStorage.getItem('tenant_slug') || ''}/login`,
            kind: 'parent',
          });
        }
      }
      // Upload photo if selected (children and reports support photo)
      if (photoFile && supportsPhoto && savedItem?.id) {
        setUploading(true);
        const fd = new FormData();
        fd.append('file', photoFile);
        await axios.post(`${API}/daycare/${tenantId}/${effectiveModuleKey}/${savedItem.id}/photo`, fd);
        setUploading(false);
      }
      setShowForm(false);
      setPhotoFile(null);
      setPhotoPreview('');
      fetchItems();
    } catch (err) {
      setUploading(false);
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo too large. Max 5MB.'); return; }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleDelete = async (item) => {
    const label = item.first_name || item.item_name || item.child_name || item.parent_name || 'this item';
    if (!window.confirm(`Delete ${label}?`)) return;
    try {
      await axios.delete(`${API}/daycare/${tenantId}/${effectiveModuleKey}/${item.id}`);
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleInviteParent = async (parentItem) => {
    try {
      const res = await axios.post(`${API}/daycare/parent-portal/invite`, {
        tenant_id: tenantId,
        parent_id: parentItem.id,
      });
      setInviteResult(res.data);
      toast.success(`Portal account created for ${res.data.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create portal account');
    }
  };

  const renderCellValue = (item, col) => {
    const val = item[col.key];
    if (col.key === 'photo_url') {
      const src = val ? `${process.env.REACT_APP_BACKEND_URL}${val}` : '';
      const initials = `${(item.first_name || '')[0] || ''}${(item.last_name || '')[0] || ''}`.toUpperCase();
      return (
        <div className="dc-cell-avatar">
          {src ? <img src={src} alt={initials} /> : <span>{initials}</span>}
        </div>
      );
    }
    if (col.badge) {
      const color = STATUS_COLORS[val] || '#6b7280';
      return (
        <span className="dc-badge" style={{ background: `${color}18`, color }} data-testid={`badge-${col.key}-${val}`}>
          {(val || '—').replace(/_/g, ' ')}
        </span>
      );
    }
    if (col.key === 'parent_names' && Array.isArray(val)) {
      return val.length > 0 ? val.join(', ') : '—';
    }
    if (col.key === 'children_count') {
      return <span className="dc-badge" style={{ background: '#3b82f618', color: '#3b82f6' }}>{val || 0}</span>;
    }
    if (col.key === 'allergies' && Array.isArray(val)) {
      return val.length > 0 ? (
        <div className="dc-tags">
          {val.map((a, i) => (
            <span key={i} className="dc-tag dc-tag-warn">{a}</span>
          ))}
        </div>
      ) : '—';
    }
    if (col.key === 'age' && val !== undefined) {
      return `${val} yr${val !== 1 ? 's' : ''}`;
    }
    if (col.key === 'amount' && val !== undefined) {
      return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    }
    return val || '—';
  };

  return (
    <div className="dc-module" data-testid={`dc-module-${moduleKey}`}>
      <div className="dc-module-header">
        <h2>{moduleName}</h2>
        <div className="dc-module-actions">
          <div className="dc-search">
            <Search size={16} />
            <input
              type="text"
              placeholder={`Search ${moduleName.toLowerCase()}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid={`dc-search-${moduleKey}`}
            />
          </div>
          <button className="dc-add-btn" style={{ background: accent, color: '#1A2139' }} onClick={openNew} data-testid={`dc-add-${moduleKey}`}>
            <Plus size={16} /> Add New
          </button>
        </div>
      </div>

      <div className="dc-table-wrap">
        {loading ? (
          <div className="dc-loading"><Loader2 size={24} className="animate-spin" /> Loading...</div>
        ) : items.length === 0 ? (
          <div className="dc-empty" data-testid={`dc-empty-${moduleKey}`}>
            <p>No {moduleName.toLowerCase()} found.</p>
            <button className="dc-add-btn" style={{ background: accent, color: '#1A2139' }} onClick={openNew}>
              <Plus size={16} /> Add first {moduleName.toLowerCase().replace(/s$/, '')}
            </button>
          </div>
        ) : (
          <table className="dc-table" data-testid={`dc-table-${moduleKey}`}>
            <thead>
              <tr>
                {columns.filter(c => !c.formOnly).map(col => <th key={col.key}>{col.label}</th>)}
                <th style={{ width: effectiveModuleKey === 'parents' ? 130 : 90 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id}>
                  {columns.filter(c => !c.formOnly).map(col => (
                    <td key={col.key}>{renderCellValue(item, col)}</td>
                  ))}
                  <td>
                    <div className="dc-row-actions">
                      {effectiveModuleKey === 'parents' && item.email && (
                        <button className="dc-icon-btn invite" onClick={() => handleInviteParent(item)} title="Create Portal Access" data-testid={`dc-invite-${item.id}`}><Mail size={15} /></button>
                      )}
                      <button className="dc-icon-btn" onClick={() => openEdit(item)} title="Edit"><Pencil size={15} /></button>
                      <button className="dc-icon-btn delete" onClick={() => handleDelete(item)} title="Delete"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="dc-count">{items.length} {moduleName.toLowerCase()} total</div>

      {showForm && (
        <div className="dc-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="dc-modal" onClick={e => e.stopPropagation()} data-testid={`dc-form-${moduleKey}`}>
            <div className="dc-modal-header">
              <h3>{editingItem ? 'Edit' : 'New'} {moduleName.replace(/s$/, '').replace(/ie$/, 'y')}</h3>
              <button onClick={() => setShowForm(false)} className="dc-icon-btn"><X size={18} /></button>
            </div>
            <div className="dc-modal-body">
              {/* Photo upload for children + daily reports */}
              {supportsPhoto && (
                <div className="dc-photo-upload" data-testid="dc-photo-upload">
                  <div className="dc-photo-preview" onClick={() => photoRef.current?.click()}>
                    {photoPreview ? (
                      <img src={photoPreview} alt={effectiveModuleKey === 'reports' ? 'Report' : 'Child'} />
                    ) : (
                      <div className="dc-photo-placeholder"><Camera size={28} /><span>{effectiveModuleKey === 'reports' ? 'Add Activity Photo' : 'Add Photo'}</span></div>
                    )}
                  </div>
                  <input ref={photoRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={handlePhotoSelect} style={{ display: 'none' }} />
                  <button type="button" className="dc-photo-btn" onClick={() => photoRef.current?.click()}>
                    <Camera size={14} /> {photoPreview ? 'Change Photo' : 'Upload Photo'}
                  </button>
                </div>
              )}
              {columns.filter(c => !c.tableOnly).map(col => (
                <div key={col.key} className="dc-form-field">
                  <label>{col.label}</label>
                  {col.type === 'select' ? (
                    <select
                      value={formData[col.key] || ''}
                      onChange={e => setFormData(p => ({ ...p, [col.key]: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      {col.options.map(opt => <option key={opt.value || opt} value={opt.value || opt}>{opt.label || opt}</option>)}
                    </select>
                  ) : col.type === 'textarea' ? (
                    <textarea
                      value={formData[col.key] || ''}
                      onChange={e => setFormData(p => ({ ...p, [col.key]: e.target.value }))}
                      rows={3}
                      placeholder={col.label}
                    />
                  ) : col.type === 'checkbox' ? (
                    <label className="dc-checkbox">
                      <input
                        type="checkbox"
                        checked={formData[col.key] || false}
                        onChange={e => setFormData(p => ({ ...p, [col.key]: e.target.checked }))}
                      />
                      <span>{col.checkLabel || col.label}</span>
                    </label>
                  ) : col.type === 'multiselect' ? (
                    <div className="dc-multiselect" data-testid={`dc-multiselect-${col.key}`}>
                      {(col.optionsFn === 'parents' ? parentsList : []).map(opt => {
                        const optId = opt.id;
                        const optLabel = `${opt.first_name} ${opt.last_name}`;
                        const selected = (formData[col.key] || []).includes(optId);
                        return (
                          <label key={optId} className={`dc-ms-option ${selected ? 'selected' : ''}`}>
                            <input type="checkbox" checked={selected} onChange={() => {
                              const current = formData[col.key] || [];
                              setFormData(p => ({
                                ...p,
                                [col.key]: selected ? current.filter(id => id !== optId) : [...current, optId]
                              }));
                            }} />
                            <span>{optLabel}</span>
                          </label>
                        );
                      })}
                      {(col.optionsFn === 'parents' ? parentsList : []).length === 0 && (
                        <p className="dc-ms-empty">No parents found. Create a parent first.</p>
                      )}
                    </div>
                  ) : col.type === 'number' ? (
                    <input
                      type="number"
                      value={formData[col.key] ?? ''}
                      onChange={e => setFormData(p => ({ ...p, [col.key]: Number(e.target.value) }))}
                      placeholder={col.label}
                    />
                  ) : (
                    <input
                      type={col.type === 'date' ? 'date' : col.type === 'time' ? 'time' : col.type === 'password' ? 'password' : 'text'}
                      value={formData[col.key] || ''}
                      onChange={e => setFormData(p => ({ ...p, [col.key]: e.target.value }))}
                      placeholder={col.label}
                      autoComplete={col.type === 'password' ? 'new-password' : 'off'}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="dc-modal-footer">
              <button className="dc-btn-cancel" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="dc-btn-save" style={{ background: accent, color: '#1A2139' }} onClick={handleSave} data-testid={`dc-save-${moduleKey}`}>
                {editingItem ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Parent Portal Account Created Modal */}
      {inviteResult && (
        <div className="dc-modal-overlay" onClick={() => setInviteResult(null)}>
          <div className="dc-modal" onClick={e => e.stopPropagation()} data-testid="dc-invite-result">
            <div className="dc-modal-header">
              <h3>{inviteResult.kind === 'staff' ? 'Staff Account Created' : 'Parent Portal Account Created'}</h3>
              <button onClick={() => setInviteResult(null)} className="dc-icon-btn"><X size={18} /></button>
            </div>
            <div className="dc-modal-body">
              <div className="dc-invite-success">
                <Mail size={32} style={{ color: '#22c55e' }} />
                <p>{inviteResult.kind === 'staff' ? 'CRM access created for' : 'Portal access created for'} <strong>{inviteResult.name}</strong></p>
              </div>
              <div className="dc-credentials-box" data-testid="dc-credentials-box">
                <div className="dc-cred-item">
                  <label>{inviteResult.kind === 'staff' ? 'Login URL' : 'Parent Portal URL'}</label>
                  <div className="dc-cred-value">
                    <span>{inviteResult.portal_url}</span>
                    <button className="dc-copy-btn" onClick={() => { navigator.clipboard.writeText(inviteResult.portal_url); toast.success('Copied!'); }}>Copy</button>
                  </div>
                </div>
                <div className="dc-cred-item">
                  <label>Email</label>
                  <div className="dc-cred-value">
                    <span>{inviteResult.email}</span>
                    <button className="dc-copy-btn" onClick={() => { navigator.clipboard.writeText(inviteResult.email); toast.success('Copied!'); }}>Copy</button>
                  </div>
                </div>
                <div className="dc-cred-item">
                  <label>Temporary Password</label>
                  <div className="dc-cred-value">
                    <span className="dc-cred-password">{inviteResult.temp_password}</span>
                    <button className="dc-copy-btn" onClick={() => { navigator.clipboard.writeText(inviteResult.temp_password); toast.success('Copied!'); }}>Copy</button>
                  </div>
                </div>
              </div>
              <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 8 }}>Share these credentials with the {inviteResult.kind === 'staff' ? 'staff member' : 'parent'}. They will be asked to change their password on first login.</p>
              <button className="dc-btn-copy-all" onClick={() => {
                const text = `Parent Portal Access\nURL: ${inviteResult.portal_url}\nEmail: ${inviteResult.email}\nPassword: ${inviteResult.temp_password}\n\nPlease change your password on first login.`;
                navigator.clipboard.writeText(text);
                toast.success('All credentials copied!');
              }} data-testid="dc-copy-all">
                Copy All Credentials
              </button>
            </div>
            <div className="dc-modal-footer">
              <button className="dc-btn-save" style={{ background: accent, color: '#1A2139' }} onClick={() => setInviteResult(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const MODULE_CONFIGS = {
  children: [
    { key: 'photo_url', label: '', type: 'text', tableOnly: true },
    { key: 'first_name', label: 'First Name', type: 'text' },
    { key: 'last_name', label: 'Last Name', type: 'text' },
    { key: 'age', label: 'Age', type: 'text', tableOnly: true },
    { key: 'date_of_birth', label: 'Date of Birth', type: 'date', formOnly: true },
    { key: 'classroom', label: 'Classroom', type: 'select', options: ['Infants', 'Toddlers', 'Preschool', 'Pre-K'] },
    { key: 'parent_names', label: 'Parents', type: 'text', tableOnly: true },
    { key: 'parent_ids', label: 'Select Parent(s)', type: 'multiselect', optionsFn: 'parents', formOnly: true },
    { key: 'allergies', label: 'Allergies', type: 'text', formOnly: true },
    { key: 'status', label: 'Status', type: 'select', badge: true, options: [
      { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }, { value: 'graduated', label: 'Graduated' }
    ], default: 'active' },
  ],
  parents: [
    { key: 'first_name', label: 'First Name', type: 'text' },
    { key: 'last_name', label: 'Last Name', type: 'text' },
    { key: 'email', label: 'Email', type: 'text' },
    { key: 'phone', label: 'Phone', type: 'text' },
    { key: 'children_count', label: 'Children', type: 'text', tableOnly: true },
    { key: 'preferred_contact', label: 'Preferred Contact', type: 'select', options: ['email', 'phone', 'sms'], formOnly: true },
    { key: 'language', label: 'Portal Language', type: 'select', options: [
      { value: 'en', label: 'English' }, { value: 'es', label: 'Español' }
    ], default: 'en', formOnly: true },
    { key: 'create_portal_access', label: 'Create Parent Portal Access', type: 'checkbox', formOnly: true, checkLabel: 'Auto-generate portal username + temporary password', default: true },
    { key: 'password', label: 'Custom Password (optional — leave blank to auto-generate)', type: 'password', formOnly: true },
    { key: 'address', label: 'Address', type: 'text', formOnly: true },
    { key: 'emergency_contact', label: 'Emergency Contact', type: 'text', formOnly: true },
    { key: 'billing_status', label: 'Billing', type: 'select', badge: true, options: [
      { value: 'current', label: 'Current' }, { value: 'overdue', label: 'Overdue' }
    ] },
  ],
  staff: [
    { key: 'first_name', label: 'First Name', type: 'text' },
    { key: 'last_name', label: 'Last Name', type: 'text' },
    { key: 'role', label: 'Role', type: 'select', options: [
      { value: 'owner', label: 'Owner' },
      { value: 'director', label: 'Director' },
      { value: 'manager', label: 'Manager' },
      { value: 'teacher', label: 'Teacher' },
      { value: 'assistant', label: 'Assistant' },
      { value: 'receptionist', label: 'Receptionist' },
    ], default: 'teacher' },
    { key: 'email', label: 'Email', type: 'text' },
    { key: 'phone', label: 'Phone', type: 'text', formOnly: true },
    { key: 'username', label: 'Username', type: 'text', formOnly: true },
    { key: 'password', label: 'Password (leave blank to auto-generate)', type: 'password', formOnly: true },
    { key: 'classroom', label: 'Assigned Classroom', type: 'select', options: ['', 'Infants', 'Toddlers', 'Preschool', 'Pre-K'] },
    { key: 'access_level', label: 'Access Level', type: 'select', options: [
      { value: 'full', label: 'Full Access' },
      { value: 'limited', label: 'Limited' },
      { value: 'read_only', label: 'Read Only' },
    ], default: 'limited', formOnly: true },
    { key: 'status', label: 'Status', type: 'select', badge: true, options: [
      { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }
    ], default: 'active' },
    { key: 'last_login_at', label: 'Last Login', type: 'text', tableOnly: true },
    { key: 'language', label: 'Language', type: 'select', options: [
      { value: 'en', label: 'English' }, { value: 'es', label: 'Español' }
    ], default: 'en', formOnly: true },
  ],
  attendance: [
    { key: 'child_name', label: 'Child', type: 'text' },
    { key: 'date', label: 'Date', type: 'date' },
    { key: 'check_in_time', label: 'Check In', type: 'time' },
    { key: 'check_out_time', label: 'Check Out', type: 'time' },
    { key: 'checked_in_by', label: 'Checked In By', type: 'text', formOnly: true },
    { key: 'status', label: 'Status', type: 'select', badge: true, options: [
      { value: 'checked_in', label: 'Checked In' }, { value: 'checked_out', label: 'Checked Out' }, { value: 'absent', label: 'Absent' }
    ], default: 'checked_in' },
  ],
  payments: [
    { key: 'parent_name', label: 'Parent', type: 'text' },
    { key: 'amount', label: 'Amount', type: 'number' },
    { key: 'type', label: 'Type', type: 'select', options: [
      { value: 'tuition', label: 'Tuition' }, { value: 'fee', label: 'Fee' }, { value: 'registration', label: 'Registration' }
    ] },
    { key: 'status', label: 'Status', type: 'select', badge: true, options: [
      { value: 'paid', label: 'Paid' }, { value: 'pending', label: 'Pending' }, { value: 'overdue', label: 'Overdue' }
    ], default: 'pending' },
    { key: 'due_date', label: 'Due Date', type: 'date' },
    { key: 'paid_date', label: 'Paid Date', type: 'date', formOnly: true },
    { key: 'method', label: 'Method', type: 'select', options: ['credit_card', 'bank_transfer', 'cash', 'check'], formOnly: true },
    { key: 'description', label: 'Description', type: 'text', formOnly: true },
  ],
  reports: [
    { key: 'child_name', label: 'Child', type: 'text' },
    { key: 'type', label: 'Type', type: 'select', badge: true, options: [
      { value: 'daily', label: 'Daily' }, { value: 'incident', label: 'Incident' }
    ], default: 'daily' },
    { key: 'date', label: 'Date', type: 'date' },
    { key: 'staff', label: 'Staff', type: 'text' },
    { key: 'mood', label: 'Mood', type: 'select', badge: true, options: [
      { value: 'happy', label: 'Happy' }, { value: 'calm', label: 'Calm' }, { value: 'tired', label: 'Tired' }, { value: 'upset', label: 'Upset' }
    ], default: 'happy' },
    { key: 'activities', label: 'Activities', type: 'textarea', formOnly: true },
    { key: 'meals', label: 'Meals', type: 'textarea', formOnly: true },
    { key: 'nap_time', label: 'Nap Time', type: 'text', formOnly: true },
    { key: 'notes', label: 'Notes', type: 'textarea', formOnly: true },
    { key: 'sent_to_parent', label: 'Send to Parent', type: 'checkbox', formOnly: true, checkLabel: 'Send report to parent' },
  ],
  supplies: [
    { key: 'item_name', label: 'Item', type: 'text' },
    { key: 'category', label: 'Category', type: 'select', options: ['Hygiene', 'Art Supplies', 'Cleaning', 'Food', 'Safety', 'Educational', 'Other'] },
    { key: 'quantity', label: 'Qty', type: 'number' },
    { key: 'reorder_level', label: 'Reorder At', type: 'number', formOnly: true },
    { key: 'status', label: 'Status', type: 'select', badge: true, options: [
      { value: 'in_stock', label: 'In Stock' }, { value: 'low', label: 'Low' }, { value: 'out_of_stock', label: 'Out of Stock' }
    ] },
    { key: 'supplier', label: 'Supplier', type: 'text' },
  ],
  notifications: [
    { key: 'type', label: 'Type', type: 'select', badge: true, options: [
      { value: 'system', label: 'System' }, { value: 'sms', label: 'SMS' }, { value: 'email', label: 'Email' }
    ], default: 'system' },
    { key: 'recipient', label: 'Recipient', type: 'text' },
    { key: 'message', label: 'Message', type: 'textarea' },
    { key: 'status', label: 'Status', type: 'select', badge: true, options: [
      { value: 'pending', label: 'Pending' }, { value: 'sent', label: 'Sent' }, { value: 'failed', label: 'Failed' }
    ], default: 'pending' },
    { key: 'created_at', label: 'Created', type: 'text', tableOnly: true },
  ],
};
